// Formato de lo que entrega la API. Los decimales llegan como TEXTO ("41500", "10800.0000",
// "12.000000") y aquí solo se les pone separador de miles y coma decimal: no se redondea, no se
// suma, no se convierte a número. Si el backend manda más decimales, se muestran todos.

function miles(entero: string): string {
  return entero.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

/** "10800.0000" → "10.800"; "1.500000" → "1,5"; "-2.50" → "-2,5"; null → "—". */
export function decimal(v: string | null | undefined): string {
  if (v === null || v === undefined || v === '') return '—';
  const m = /^(-?)(\d+)(?:\.(\d+))?$/.exec(v.trim());
  if (!m) return v;
  const [, signo, entero, fraccion] = m;
  const f = (fraccion ?? '').replace(/0+$/, '');
  return `${signo}${miles(entero!)}${f ? `,${f}` : ''}`;
}

/** Pesos brutos: "41500" → "$41.500". */
export function pesos(v: string | null | undefined): string {
  const d = decimal(v);
  return d === '—' ? d : d.startsWith('-') ? `-$${d.slice(1)}` : `$${d}`;
}

/** Tasa como fracción del backend ("0.190000") → "19 %" (solo mueve la coma: es texto). */
export function tasa(v: string | null | undefined): string {
  if (!v) return '—';
  const m = /^0?\.(\d+)$|^(\d+)\.(\d+)$/.exec(v);
  if (!m) return v;
  const entero = m[2] ?? '0';
  const frac = (m[1] ?? m[3] ?? '').padEnd(2, '0');
  const pct = `${entero === '0' ? '' : entero}${frac.slice(0, 2)}`.replace(/^0+(?=\d)/, '') || '0';
  const resto = frac.slice(2).replace(/0+$/, '');
  return `${pct}${resto ? `,${resto}` : ''} %`;
}

export function rut(numero: string | null | undefined, dv?: string | null): string {
  if (!numero) return '—';
  const [n, d] = numero.includes('-') ? numero.split('-') : [numero, dv];
  return `${miles(n ?? '')}${d ? `-${d}` : ''}`;
}

const fmtFechaHora = new Intl.DateTimeFormat('es-CL', {
  timeZone: 'America/Santiago',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});
export function fechaHora(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : fmtFechaHora.format(d);
}

/** "2026-09-30" → "30-09-2026" (fecha de negocio: sin zona horaria). */
export function fecha(iso: string | null | undefined): string {
  if (!iso) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : iso;
}

/**
 * Lo que el usuario digita en una cantidad o precio → el texto decimal que exige la API.
 * Acepta coma o punto decimal y puntos de miles ("1.234,5"); devuelve null si no es un número
 * positivo bien formado. No convierte a number.
 */
export function entradaDecimal(texto: string, decimales = 6): string | null {
  let t = texto.trim().replace(/\s/g, '');
  if (!t) return null;
  if (t.includes(',')) t = t.replace(/\./g, '').replace(',', '.');
  const m = /^(\d{1,14})(?:\.(\d+))?$/.exec(t);
  if (!m) return null;
  const entero = m[1]!.replace(/^0+(?=\d)/, '');
  const frac = m[2] ?? '';
  if (frac.length > decimales) return null;
  return frac ? `${entero}.${frac}` : entero;
}

/** "12.000000" → "12"; "1.500" → "1.5"; "10" → "10" (solo quita ceros de la fracción). */
export function sinCerosDecimales(v: string): string {
  return v.includes('.') ? v.replace(/0+$/, '').replace(/\.$/, '') : v;
}

/** ¿El texto decimal es cero? (comparación de texto, sin convertir). */
export function esCero(v: string | null | undefined): boolean {
  return !v || /^-?0*(\.0*)?$/.test(v);
}

export const NOMBRE_CONDICION: Record<string, string> = {
  contado: 'Contado',
  cheque_al_dia: 'Cheque al día',
  cheque_a_plazo: 'Cheque a plazo',
  credito: 'Crédito',
  transferencia: 'Transferencia',
  cheque: 'Cheque',
};

export const NOMBRE_CANAL: Record<string, string> = {
  VENDEDOR: 'Vendedor',
  CALL_CENTER: 'Call center',
  MESON: 'Mesón',
  RUTA: 'Ruta',
  B2B: 'B2B',
  POS: 'POS',
};

/** STOCK_POR_REVISAR: el saldo de ese producto en esa bodega no calza con Random sin causa explicada. */
export const STOCK_POR_REVISAR =
  'Stock por revisar: no calza con el sistema anterior sin causa explicada; no confíes en este disponible';
